import React from 'react';
import PropTypes from 'prop-types';
import { Box, Tooltip } from '@mui/material';

// A single slim selectable chip for the Personal Reply greeting / sign-off rows
// (mock .chip / .chip.on). Selected = primary tint; disabled = muted + not
// clickable (used when a sign-off's template isn't approved). Keyboard-operable.

const CHIP = {
    primary: '#00A76F',
    primaryDark: '#007B55',
    primaryTint: '#00a76f14',
    chipBorder: '#cfd6dc',
    chipText: '#54606b',
    textDisabled: '#919EAB',
    paper: '#FFFFFF',
};

const chipTextColor = (active, disabled) => {
    if (disabled) return CHIP.textDisabled;
    return active ? CHIP.primaryDark : CHIP.chipText;
};

const chipSx = (active, disabled) => ({
    flex: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    textAlign: 'center',
    fontSize: '11.5px',
    fontWeight: 600,
    lineHeight: 1.3,
    py: '5px',
    px: '6px',
    borderRadius: '7px',
    border: `1px solid ${active ? CHIP.primary : CHIP.chipBorder}`,
    backgroundColor: active ? CHIP.primaryTint : CHIP.paper,
    color: chipTextColor(active, disabled),
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? 'default' : 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    transition: 'border-color 0.15s, background-color 0.15s',
    '&:hover': disabled ? {} : { borderColor: CHIP.primary, backgroundColor: CHIP.primaryTint },
    '&:focus-visible': { outline: `2px solid ${CHIP.primary}`, outlineOffset: 1 },
});

function QuickReplyChip({ label, active, disabled, tooltip, onSelect }) {
    const handleKeyDown = (e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
        }
    };
    const chip = (
        <Box
            role="button"
            aria-pressed={active}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : 0}
            onClick={disabled ? undefined : onSelect}
            onKeyDown={handleKeyDown}
            sx={chipSx(active, disabled)}
        >
            {label}
        </Box>
    );
    // Tooltip is used to explain WHY a chip is disabled (e.g. greeting variant
    // still awaiting WhatsApp approval). Wraps the chip directly (no extra DOM),
    // so the flex:1 layout is preserved.
    if (!tooltip) return chip;
    return (
        <Tooltip title={tooltip} arrow enterTouchDelay={0} disableInteractive>
            {chip}
        </Tooltip>
    );
}

QuickReplyChip.propTypes = {
    label: PropTypes.node.isRequired,
    active: PropTypes.bool,
    disabled: PropTypes.bool,
    // Optional hover/tap explanation — mainly used on disabled chips.
    tooltip: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
};

QuickReplyChip.defaultProps = {
    active: false,
    disabled: false,
    tooltip: '',
};

export default QuickReplyChip;
